import React from "react";
import { useTranslation } from "react-i18next";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import styles from "./styles.module.scss";

interface IProps {
  qrUrl?: string;
  quickConnectUrl?: string;
}

const QRCodeConfig = ({ qrUrl, quickConnectUrl }: IProps) => {
  const { t } = useTranslation();
  const { copied, copyToClipboard } = useCopyToClipboard();

  // Generate a QR code image from the destination URL
  const qrImageUrl = qrUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`
    : undefined;

  const handleDownload = () => {
    if (!qrImageUrl) {
      return;
    }
    const link = document.createElement("a");
    link.href = qrImageUrl;
    link.download = "workflow-qr-code.png";
    link.click();
  };

  const handleShare = async () => {
    if (!quickConnectUrl) {
      return;
    }
    if (navigator.share) {
      await navigator.share({
        title: t("Workflow QR Code"),
        url: quickConnectUrl,
      });
    } else {
      copyToClipboard(quickConnectUrl);
    }
  };

  const handleCopyLink = () => {
    if (!qrUrl) {
      return;
    }
    copyToClipboard(qrUrl);
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {t(
          "Download this QR code to share with athletes. When scanned, it will launch the workflow for them.",
        )}
      </p>

      <div className={styles.qr_code}>
        {qrImageUrl ? (
          <img src={qrImageUrl} alt="QR Code" className={styles.qr_image} />
        ) : (
          <div className={styles.qr_placeholder}>{t("No QR code available")}</div>
        )}
      </div>

      <button className={styles.download_btn} onClick={handleDownload}>
        <i className="ico ico-download" />
        {t("Download QR Code")}
      </button>

      <div className={styles.url_section}>
        <h3 className={styles.url_title}>{t("Quick Connect URL")}</h3>
        <p className={styles.url_description}>
          {t(
            "This link also starts the workflow. Share it as an alternative to the QR code.",
          )}
        </p>
        <div className={styles.url_display}>
          <i className="ico ico-link" />
          <span>{qrUrl || t("Not available")}</span>
        </div>
      </div>

      <div className={styles.action_buttons}>
        <button className={styles.action_btn} onClick={handleShare}>
          <i className="ico ico-open" />
          {t("Share")}
        </button>
        <button className={styles.action_btn} onClick={handleCopyLink}>
          <i className="ico ico-copy" />
          {copied ? t("Copied!") : t("Copy Link")}
        </button>
      </div>
    </div>
  );
};

export default QRCodeConfig;
